<template>
    <div class="contract-verification">
        <template v-if="$route.name === 'contractVerification'">
            <v-card elevation="2" class="mx-auto px-15 py-10 red-s" max-width="670">
                <v-card-title class="text-primary font-weight-bold">
                    التحقق من وثيقة العقد
                </v-card-title>
                <v-card-subtitle class="text-dark">
                    خدمة تتيح للمستفيد التحقق من صحة وثائق العقود وتفاصيلها بما في ذلك من مدة العقد وأطراف العقد والبنود وغيرها
                </v-card-subtitle>
                <validation-observer ref="formObserver" v-slot="{ invalid }">
                    <v-form>
                        <v-container>
                            <validation-provider v-slot="{ errors }" name="contractCode" rules="required|numeric|max:19">
                                <v-input class="mb-2" :error-messages="errors">
                                    <div class="input-wraper">
                                        <label for="contractCode" class="Required text-dark">رقم وثيقة العقد</label>
                                        <input id="contractCode" name="contractCode" v-numbers type="text" inputmode="numeric"
                                               placeholder="رقم وثيقة العقد" maxlength="19" v-model="contractVerification.contractCode" required>
                                    </div>
                                </v-input>
                            </validation-provider>
                            <validation-provider ref="dateProvider" v-slot="{ errors }" name="contractCreationDate" rules="required">
                                <v-input class="mb-2" :error-messages="errors" required>
                                    <DropDownCalendarComponent label="تاريخ إصدار الوثيقة (هجري/ميلادي)" 
                                                               :futureYearsCount="0" :yearsCount="10" isRequired="true" maxDate="today" minDate="1444-1-1"
                                                               @dateChanged="onContractCreationDateChanged" class="text-dark">
                                    </DropDownCalendarComponent>
                                </v-input>
                            </validation-provider>
                            <validation-provider v-slot="{ errors }" name="requesterNationalId"
                                                 rules="required|numeric|max:10|requesterNationalId" v-if="!loggedIn && isVerifyEnabled">
                                <v-input class="mb-2" :error-messages="errors">
                                    <div class="input-wraper">
                                        <label for="requesterNationalId" class="Required text-dark">رقم هوية المتحقق</label>
                                        <input id="requesterNationalId" name="requesterNationalId" v-numbers type="text" inputmode="numeric"
                                               placeholder="رقم هوية المتحقق" maxlength="10" v-model="contractVerification.requesterNationalId">
                                    </div>
                                </v-input>
                            </validation-provider>
                            <validation-provider ref="recaptchaProvider" v-slot="{ errors }" name="recaptcha" rules="required"
                                                 v-if="!loggedIn && reCaptchaEnabled">
                                <v-input :error-messages="errors" v-model="isReCaptchaVerified">
                                    <Recaptcha ref="recaptcha" :sitekey="reCaptchaSiteKey" :verify="onReCaptchaVerify"
                                               :expired="onReCaptchaExpired" />
                                </v-input>
                            </validation-provider>
                        </v-container>
                    </v-form>
                    <v-card-actions>
                        <v-btn x-large color="secondary" @click="onVerify" :disabled="invalid">
                            تحقق
                            <v-icon right dark>
                                mdi-arrow-left-thin
                            </v-icon>
                        </v-btn>
                    </v-card-actions>
                </validation-observer>
            </v-card>
            <div v-if="!loggedIn">
                <div v-if="useNafathForContractVerification" class="nafath-container">
                    <NafathNumberChallenge v-model="openNafath"
                                           :isLoading="openNafath"
                                           :send-request="sendNafathRequest"
                                           :check-request="checkNafathStatus"
                                           :id-number="contractVerification.requesterNationalId"
                                           :options="{ interval: 5000, timeout: 300000 }"
                                           @status-changed="onStatusChanged"
                                           @loading="onLoading"
                                           ref="nafathChallenge">
                    </NafathNumberChallenge>
                    <!--check-request="api/Contract/NafathCheckStatusByAPI"-->
                    <!--:check-request="checkNafathStatus"-->
                    <!--<img v-if="openNafath" id="nafathLoading" class="loader" alt="najiz loader" src="../../../public/img/Najiz-loading.gif">-->
                </div>
                <div v-else>
                    <OtpComponent ref="otp" :nationalId="contractVerification.requesterNationalId" @verify="verify" />
                </div>
            </div>
        </template>
        <router-view></router-view>
    </div>
</template>

<script lang="ts">
    import { Component, Vue } from 'vue-property-decorator';
    import { Inject } from 'vue-di-container';
    import axios from 'axios';
    import Recaptcha from '@t2/recaptcha';
    import { Toaster } from '@/services/toast';
    import LoaderService from '@/services/loader';

    import DropDownCalendarComponent from '@/components/dropDownCalendar.vue';
    import { DDCalendarDate } from '@/models/common';
    import UserService from '@/services/user';
    import ExternalService from '@/services/external';
    import { ContractVerificationModel } from '@/models/contract-verification.mode';
    import { OTPRefrenceDataModel } from '@/models/OTPRefrenceDataModel';
    import { Constants } from '@/utils/constants';
    import RecaptchaService from '@/services/recaptcha';
    import OtpComponent from '@/components/OTP/OtpComponent.vue';
    import ContractService from '@/services/contract.service';
    import { NafathRequestDTO, NafathResponseModel, NafathRequestErrorModel, NafathCheckStatusRequestDTO, NafathCheckStatusResponseModel } from '@/models/NafathModel';
    import { NafathCheckStatuses } from '@/enums/NafathCheckStatuses';

    /**
     * Get the current logged-in user.
     * @param callback A function called after the response is resolved.
     */
    function getLoggedInUser(callback: (res: any) => void) {
        axios.get(Constants.USER_INFO_PATH).then((res) => callback(res.data)).catch(() => callback(null));
    }

    @Component({
        components: {
            DropDownCalendarComponent,
            Recaptcha,
            OtpComponent,
        },
        diProvide: [
            UserService,
            ContractService,
            ExternalService,
            RecaptchaService,
        ],
        beforeRouteEnter(to, from, next) {
            getLoggedInUser((data: any) => {
                next((vm: any) => {
                    vm.loggedIn = !!data.username;

                    if (vm.loggedIn) {
                        vm.contractVerification.requesterNationalId = data.username;
                    }
                });
            });
        },
    })
    export default class ContractVerificationSearch extends Vue {
        /** The contract service instance. */
        @Inject(ContractService) public contractService!: ContractService;

        /** The user service instance. */
        @Inject(UserService) public userService!: UserService;

        /** The external service instance. */
        @Inject(ExternalService) public externalService!: ExternalService;

        /** The recaptcha service instance. */
        @Inject(RecaptchaService) public recaptchaService!: RecaptchaService;

        /** Holds the form data. */
        contractVerification!: ContractVerificationModel;


        @Inject(LoaderService) public loaderService!: LoaderService;

        /** Used to determine if the user is logged-in or not. */
        loggedIn = false;

        // Recaptcha

        /** Determines if the reCaptcha is enabled or not. */
        reCaptchaEnabled = false;

        /** The reCaptcha site key for Najiz. */
        reCaptchaSiteKey = '';

        /** Determines that the captcha is verified from the backend services. */
        isReCaptchaVerified = false;
        useNafathForContractVerification = false;
        isVerifyEnabled = false;
        isNafathLoading = false;
        openNafath = false;
        sendRequest!: Request;
        nafathRequestDTO!: NafathRequestDTO;
        nafathResponseModel!: NafathResponseModel;
        nafathCheckStatusRequestDTO!: NafathCheckStatusRequestDTO;
        constructor() {
            super();

            this.contractVerification = {
                contractCode: '',
                contractCreationDate: '',
                requesterNationalId: '',
            };
            this.nafathRequestDTO = {
                contractCode: '',
                contractCreationDate: '',
                userIdentityNumber: ''
            };
            //this.nafathResponseModel = {
            //    contractCode: '',
            //    errorMessage: '',
            //    nafathRequestErrorModel: { code: '', error: '', error_description: '' },
            //    random: '',
            //    statusCode: null,
            //    transId: '',
            //    userIdentityNumber: ''
            //}
            this.nafathCheckStatusRequestDTO = {
                transId: '',
                random: '',
                userIdentityNumber: '',
                contractCode: ''
            };
        }

        async created(): Promise<void> {

            if (!this.loggedIn) {
                // Get the recaptcha site key.
                this.reCaptchaSiteKey = await this.recaptchaService.key();
                this.reCaptchaEnabled = await this.recaptchaService.isEnabled();
                this.useNafathForContractVerification = await this.recaptchaService.useNafathForContractVerification();
                this.isVerifyEnabled = await this.recaptchaService.isVerifyEnabled();
            }
        }

        /** Fires when the contract creation date has changed. */
        onContractCreationDateChanged(date: DDCalendarDate): void {
            this.contractVerification.contractCreationDate = date.gregDate;
            (this.$refs.dateProvider as any).validate(date.gregDate);
        }

        /**
         * Initiate the contract verification process.
         * @returns void
         */
        async onVerify(): Promise<void> {

            //const isValid = await (this.$refs.formObserver as any).validate();

            //if (!isValid) return;

            if (!this.loggedIn && this.isVerifyEnabled) {
                if (this.useNafathForContractVerification) {
                    this.nafathRequestDTO.contractCode = this.contractVerification.contractCode;
                    this.nafathRequestDTO.contractCreationDate = this.contractVerification.contractCreationDate;
                    this.nafathRequestDTO.userIdentityNumber = this.contractVerification.requesterNationalId;

                    this.sendRequest = new Request("api/Contract/NafathRequestByAPI", {
                        method: "POST",
                        body: JSON.stringify(this.nafathRequestDTO),
                        headers: { "Content-Type": "application/json" }
                    });
                    this.openNafath = true;
                }
                else {
                    await (this.$refs.otp as any).sendOtp();
                }
            }
            else {
                this.verify();
            }
        }

        sendNafathRequest() {
            return this.contractService.NafathRequestByAPI(this.nafathRequestDTO).then((response) => {
                //this.nafathResponseModel = {
                //    contractCode: response?.contractCode,
                //    errorMessage: response?.errorMessage,
                //    nafathRequestErrorModel: {
                //        code: response?.nafathRequestErrorModel?.code,
                //        error: response?.nafathRequestErrorModel?.error,
                //        error_description: response?.nafathRequestErrorModel?.error_description
                //    },
                //    random: response?.random,
                //    statusCode: response?.statusCode,
                //    transId: response?.transId,
                //    userIdentityNumber: response?.userIdentityNumber
                //}
                this.nafathCheckStatusRequestDTO = {
                    transId: response?.transId,
                    random: response?.random,
                    userIdentityNumber: response?.userIdentityNumber,
                    contractCode: response?.contractCode
                };
                if (response.statusCode === 200) {

                    return {
                        succeeded: true,
                        data: response
                    }
                }
                else {

                    return {
                        succeeded: false,
                        data: { errorMessage: response.errorMessage }
                    }
                }

            }).catch((error) => {
                return {
                    succeeded: false,
                    data: error
                }
            });;
        }

        checkNafathStatus() {
            return this.contractService.NafathCheckStatusByAPI(this.nafathCheckStatusRequestDTO).then((response) => {
                    //response.statusCode=200;
                if (response?.status === NafathCheckStatuses.WAITING || response?.status === NafathCheckStatuses.COMPLETED) {
                    return {
                        succeeded: true,
                        data: response
                    }
                }
                else {
                    console.log("response:",response)
                    return {
                        succeeded: true,
                        data: response
                    }
                    this.openNafath = false;
                }

            });
        }

        //check-request
        onStatusChanged(payload: any) {
            console.log('Challenge completed with status:', payload.status)
            if (payload?.status === "COMPLETED") {
                this.verify();

                //this.loaderService.HideLoader();

                this.openNafath = false;
            }
            else if (payload?.status === "EXPIRED" || payload?.status === "REJECTED") {
                this.openNafath = false;
            }
            this.isNafathLoading = false;
        }

        onLoading(isLoading: boolean) {
            console.log('Loading state:', isLoading)
        }

        /**
         * Checks the verification of a contract based on the input data form.
         * @param otpData The OTP data for the anonymous user.
         */
        verify(otpData: OTPRefrenceDataModel | null = null): void {
            if (otpData) {
                this.contractVerification.oTPRefrenceDataDTO = otpData;
            }

            this.contractVerification.isloggedIn = this.loggedIn;


            this.contractService.verify(this.contractVerification).then((res) => {

                var resultObjectt = JSON.stringify(res.model);

                // Store the contract locally in session storage.
                sessionStorage.setItem('contract', resultObjectt);

                if (typeof resultObjectt !== 'undefined') {

                    this.$router.push({ name: 'contractVerificationResult' });
                    this.reset();

                    return;

                }
                Toaster.error({ message: 'الخدمه غير متاحه ' });


            }).catch((err) => {
                //alert(err);
                //if (err.data.statusCode === 404) {
                //    (this.$refs.otp as any).reset();
                //}
            });
        }

        async onReCaptchaVerify(response: any): Promise<void> {

            // Verify the captcha from the backend services.
            const verified = await this.recaptchaService.verify(response);
            this.isReCaptchaVerified = verified;
            (this.$refs.recaptchaProvider as any).validate(verified);

            if (!verified) {
                (this.$refs.recaptcha as any).reset();
            }
        }

        /**
         * Fires when the reCaptcha has expired.
         */
        onReCaptchaExpired(): void {
            this.isReCaptchaVerified = false;
            (this.$refs.recaptchaProvider as any).validate(false);
            (this.$refs.otp as any).reset();
        }

        /**
         * Reset everything.
         */
        private reset(): void {
            this.contractVerification = {
                contractCode: '',
                contractCreationDate: '',
                requesterNationalId: this.loggedIn ? this.contractVerification.requesterNationalId : '',
            };
            (this.$refs.otp as any).reset();
        }
    }
</script>


<style scoped>
    .nafath-container {
        position: relative; /* Position relative for absolute children */
    }

    .loader {
        position: fixed; /* Position fixed to screen */
        top: 65%; /* Center vertically */
        left: 50%; /* Center horizontally */
        transform: translate(-50%, -50%); /* Adjust to center */
        z-index: 1000; /* Ensure it's above all other elements */
        display: block; /* Ensure it is block-level */
        width: 50px; /* Adjust size as needed */
        height: auto; /* Maintain aspect ratio */
    }
</style>